import { useCallback, useState, useMemo } from 'react';
import { Box, Button, Menu, MenuItem, ListItemText, ListItemIcon, Divider, Tooltip } from '@mui/material';
import {
  ArrowDropDown as DropdownIcon,
  PauseCircle as HoldIcon,
  PlayCircle as UnholdIcon,
  Warning as UrgentIcon,
  Delete as DeleteIcon,
  Visibility as ViewerIcon,
} from '@mui/icons-material';
import type { Study } from '../../api/types';
import { STUDY_ACTIONS, MENU_GROUPS, STANDALONE_ACTIONS, type StudyActionDef } from './studyActions';
import { useRefreshStudies } from './useStudies';
import { getAllViewers, getDefaultViewer } from '../viewer/viewerRegistry';
import { useUIStore } from '../../stores/uiStore';
import { gwtStyles } from '../../config/theme';
import { pushError } from '../../stores/notificationStore';

/** Safe action lookup — falls back to a disabled stub if not found */
function findAction(list: StudyActionDef[], id: string): StudyActionDef {
  return list.find((a) => a.id === id) ?? STUDY_ACTIONS.find((a) => a.id === id) ?? {
    id,
    label: id,
    group: null,
    backendReady: false,
    handler: async () => false,
  };
}

interface ActionToolbarProps {
  selectedStudies: Study[];
}

/**
 * GWT-matching action toolbar — mirrors ActionToolbar.java.
 *
 * Layout (from GWT blue theme):
 *   [Hold] [Unhold] [Urgent] [Delete] [Findings ▾] [Status ▾] [Actions ▾] [Reporting ▾] [HL7 ▾] [QC ▾] [Storage ▾] [Admin ▾] [Refresh]
 *
 * Each dropdown opens a menu with the actions in the corresponding MenuStudyAction group.
 * Actions with backendReady=false show a tooltip explaining they need backend work.
 */
export const ActionToolbar = ({ selectedStudies }: ActionToolbarProps) => {
  const refreshStudies = useRefreshStudies();
  const hasSelection = selectedStudies.length > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        height: 28,
        overflow: 'hidden',
      }}
    >
      {/* View button — opens selected study in viewer */}
      <ViewButton selectedStudies={selectedStudies} disabled={!hasSelection} />

      <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />

      {/* Standalone quick-access buttons */}
      <QuickActionButton
        icon={<HoldIcon sx={{ fontSize: 14 }} />}
        label="Hold"
        action={findAction(STANDALONE_ACTIONS, 'HOLDSTUDY')}
        studies={selectedStudies}
        disabled={!hasSelection}
        onDone={refreshStudies}
      />
      <QuickActionButton
        icon={<UnholdIcon sx={{ fontSize: 14 }} />}
        label="Unhold"
        action={findAction(STANDALONE_ACTIONS, 'UNHOLDSTUDY')}
        studies={selectedStudies}
        disabled={!hasSelection}
        onDone={refreshStudies}
      />
      <QuickActionButton
        icon={<UrgentIcon sx={{ fontSize: 14 }} />}
        label="Urgent"
        action={findAction(STUDY_ACTIONS, 'MARKURGENT')}
        studies={selectedStudies}
        disabled={!hasSelection}
        onDone={refreshStudies}
      />
      <QuickActionButton
        icon={<DeleteIcon sx={{ fontSize: 14 }} />}
        label="Delete"
        action={findAction(STUDY_ACTIONS, 'DELETESTUDYALL')}
        studies={selectedStudies}
        disabled={!hasSelection}
        onDone={refreshStudies}
      />

      <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />

      {/* Menu group dropdowns */}
      {MENU_GROUPS.map((group) => (
        <MenuGroupButton
          key={group.id}
          label={group.label}
          actions={group.actions}
          studies={selectedStudies}
          disabled={!hasSelection}
          onDone={refreshStudies}
        />
      ))}
    </Box>
  );
};

/* ── Quick-access button ── */

interface QuickActionButtonProps {
  icon: React.ReactNode;
  label: string;
  action: StudyActionDef;
  studies: Study[];
  disabled: boolean;
  onDone: () => void;
}

const QuickActionButton = ({ icon, label, action, studies, disabled, onDone }: QuickActionButtonProps) => {
  const [loading, setLoading] = useState(false);

  const tooltipText = useMemo(() => {
    if (!action.backendReady) return `${label} — needs backend JSON-RPC method`;
    if (disabled) return `${label} — select a study first`;
    return label;
  }, [action.backendReady, disabled, label]);

  const handleClick = useCallback(async () => {
    if (disabled || !action.backendReady) return;
    setLoading(true);
    try {
      await action.handler(studies);
      onDone();
    } catch (e) {
      pushError(`Action "${action.label}" failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setLoading(false);
    }
  }, [action, disabled, studies, onDone]);

  return (
    <Tooltip title={tooltipText}>
      <span>
        <Button
          size="small"
          onClick={handleClick}
          disabled={disabled || loading || !action.backendReady}
          sx={{
            minWidth: 0,
            px: 0.75,
            py: 0.25,
            fontSize: 10,
            lineHeight: 1.2,
            textTransform: 'none',
            color: gwtStyles.textSecondary,
            border: `1px solid ${gwtStyles.borderSubtle}`,
            borderRadius: '4px',
            '&:hover': { color: gwtStyles.textPrimary, borderColor: gwtStyles.accentPrimary, backgroundColor: 'rgba(91,141,239,0.08)' },
            gap: 0.25,
          }}
        >
          {icon}
          {label}
        </Button>
      </span>
    </Tooltip>
  );
};

/* ── Menu group dropdown ── */

interface MenuGroupButtonProps {
  label: string;
  actions: StudyActionDef[];
  studies: Study[];
  disabled: boolean;
  onDone: () => void;
}

const MenuGroupButton = ({ label, actions, studies, disabled, onDone }: MenuGroupButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleAction = useCallback(
    async (action: StudyActionDef) => {
      handleClose();
      if (!action.backendReady) return;
      try {
        await action.handler(studies);
        onDone();
      } catch (e) {
        pushError(`Action "${action.label}" failed: ${e instanceof Error ? e.message : String(e)}`);
      }
    },
    [studies, onDone, handleClose],
  );

  return (
    <>
      <Button
        size="small"
        onClick={handleOpen}
        disabled={disabled}
        endIcon={<DropdownIcon sx={{ fontSize: 14, ml: -0.5 }} />}
        sx={{
          minWidth: 0,
          px: 0.75,
          py: 0.25,
          fontSize: 10,
          lineHeight: 1.2,
          textTransform: 'none',
          color: gwtStyles.textSecondary,
          border: `1px solid ${gwtStyles.borderSubtle}`,
          borderRadius: '4px',
          '&:hover': { color: gwtStyles.textPrimary, borderColor: gwtStyles.accentPrimary, backgroundColor: 'rgba(91,141,239,0.08)' },
        }}
      >
        {label}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              minWidth: 160,
              borderRadius: '6px',
              border: `1px solid ${gwtStyles.borderSubtle}`,
              backgroundColor: gwtStyles.popupBg,
            },
          },
        }}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.id}
            onClick={() => handleAction(action)}
            disabled={!action.backendReady}
            sx={{
              fontSize: 12,
              py: 0.5,
              minHeight: 28,
              '&.Mui-disabled': {
                opacity: 0.5,
              },
            }}
          >
            <ListItemText
              primary={action.label}
              primaryTypographyProps={{ fontSize: 12 }}
            />
            {!action.backendReady && (
              <Tooltip title="Needs backend JSON-RPC method">
                <Box
                  component="span"
                  sx={{ ml: 1, fontSize: 10, color: 'text.disabled' }}
                >
                  ⚠
                </Box>
              </Tooltip>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/* ── View button — split button with viewer picker ── */

const ViewButton = ({ selectedStudies, disabled }: { selectedStudies: Study[]; disabled: boolean }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const openDynamicTab = useUIStore((s) => s.openDynamicTab);
  const viewers = getAllViewers();
  const defaultViewer = getDefaultViewer();
  const hasMultipleViewers = viewers.length > 1;

  const openInViewer = useCallback(
    (viewerId: string) => {
      setAnchorEl(null);
      for (const study of selectedStudies) {
        openDynamicTab({
          id: `viewer-${viewerId}-${study.uid}`,
          label: `${study.name || 'Study'} - ${study.acc || study.uid}`.slice(0, 40),
          type: 'viewer',
          study,
          viewerId,
          closeable: true,
        });
      }
    },
    [selectedStudies, openDynamicTab],
  );

  const handleClick = useCallback(() => {
    if (hasMultipleViewers) return; // dropdown handles it
    openInViewer(defaultViewer.id);
  }, [hasMultipleViewers, defaultViewer, openInViewer]);

  const handleDropdown = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={disabled ? 'Select a study first' : `View in ${defaultViewer.name}`}>
          <span>
            <Button
              size="small"
              onClick={hasMultipleViewers ? handleDropdown : handleClick}
              disabled={disabled}
              endIcon={hasMultipleViewers ? <DropdownIcon sx={{ fontSize: 14, ml: -0.5 }} /> : undefined}
              sx={{
                minWidth: 0,
                px: 0.75,
                py: 0.25,
                fontSize: 10,
                lineHeight: 1.2,
                textTransform: 'none',
                color: gwtStyles.accentPrimary,
                border: `1px solid ${gwtStyles.accentPrimary}`,
                borderRadius: '4px',
                fontWeight: 600,
                '&:hover': { backgroundColor: 'rgba(91,141,239,0.12)' },
                gap: 0.25,
              }}
            >
              <ViewerIcon sx={{ fontSize: 14 }} />
              View
            </Button>
          </span>
        </Tooltip>
      </Box>
      {hasMultipleViewers && (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          slotProps={{
            paper: {
              sx: {
                minWidth: 180,
                borderRadius: '6px',
                border: `1px solid ${gwtStyles.borderSubtle}`,
                backgroundColor: gwtStyles.popupBg,
              },
            },
          }}
        >
          {viewers.map((viewer) => (
            <MenuItem
              key={viewer.id}
              onClick={() => openInViewer(viewer.id)}
              sx={{ fontSize: 12, py: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 24 }}>
                <ViewerIcon sx={{ fontSize: 14, color: viewer.id === defaultViewer.id ? gwtStyles.accentPrimary : 'text.secondary' }} />
              </ListItemIcon>
              <ListItemText
                primary={viewer.name}
                secondary={viewer.id === defaultViewer.id ? 'Default' : undefined}
                primaryTypographyProps={{ fontSize: 12 }}
                secondaryTypographyProps={{ fontSize: 9, color: gwtStyles.accentPrimary }}
              />
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
};
